import React, { useState, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import TeamDisplay from "@/components/TeamDisplay.jsx";
import { useBrandConfig } from "@/features/brandDemo/BrandConfigContext.jsx";
import { BrandLogo } from "@/components/brandDemo/BrandLogo.jsx";

const CDN = "https://cdn.prod.website-files.com/64e75b79eaa8eb84e0f6f6d7";
const BG_PHOTO = `${CDN}/64f764f5cda901ea21ae4fd1_photo-marquee-1-p-800.jpg`;

export const GruppomimoStepWelcome = ({ onStart, contestData }) => {
    const { t } = useTranslation();
    const { brand, welcome } = useBrandConfig();

    const [isVisible, setIsVisible] = useState(false);
    const [splashState, setSplashState] = useState('animating');

    const homeTeam = contestData?.fixture?.homeTeam || { name: "France", shortName: "FRA", logoUrl: "https://flagcdn.com/w160/fr.png" };
    const awayTeam = contestData?.fixture?.awayTeam || { name: "Argentine", shortName: "ARG", logoUrl: "https://flagcdn.com/w160/ar.png" };

    useEffect(() => {
        const exitTimer = setTimeout(() => setSplashState('exiting'), 1400);
        const doneTimer = setTimeout(() => { setSplashState('done'); setIsVisible(true); }, 1900);
        return () => { clearTimeout(exitTimer); clearTimeout(doneTimer); };
    }, []);

    const teamProps = {
        flagW: "w-20 min-[380px]:w-24",
        flagH: "h-14 min-[380px]:h-16",
        textColor: "text-black font-black text-lg min-[380px]:text-xl uppercase tracking-tighter",
    };

    const customStyles = `
        @keyframes gm-float {
            0%, 100% { transform: translateY(0px); }
            50%       { transform: translateY(-6px); }
        }
        .animate-gm-float { animation: gm-float 4s ease-in-out infinite; }

        @keyframes gm-boom {
            0%   { transform: scale(0.4); opacity: 0; filter: blur(16px); }
            50%  { transform: scale(1.08); opacity: 1; filter: blur(0); }
            100% { transform: scale(1);   opacity: 1; }
        }
        .animate-gm-boom { animation: gm-boom 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }

        @keyframes gm-flash {
            0%   { transform: translateX(-220%) rotate(40deg); }
            100% { transform: translateX(220%)  rotate(40deg); }
        }
        .animate-gm-flash { animation: gm-flash 2.4s ease-in-out infinite; }

        .stagger-1 { transition-delay: 80ms; }
        .stagger-2 { transition-delay: 180ms; }
        .stagger-3 { transition-delay: 280ms; }
        .stagger-4 { transition-delay: 380ms; }

        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
    `;

    return (
        <div className="absolute inset-0 z-50 font-body overflow-hidden flex flex-col" style={{ backgroundColor: brand.colors.pageBg }}>
            <style dangerouslySetInnerHTML={{ __html: customStyles }} />

            {/* ── SPLASH ─────────────────────────────────────────────── */}
            {splashState !== 'done' && (
                <div
                    className={`absolute inset-0 z-[100] flex flex-col items-center justify-center transition-all duration-700 ease-[cubic-bezier(0.9,0,0.1,1)]
                        ${splashState === 'exiting' ? 'scale-[1.5] opacity-0' : 'scale-100 opacity-100'}`}
                    style={{ backgroundColor: brand.colors.pageBg }}
                >
                    <div className="absolute w-32 h-32 rounded-full opacity-30 animate-ping" style={{ backgroundColor: brand.colors.accent }} />
                    <div className="relative overflow-hidden flex items-center justify-center">
                        <BrandLogo className="w-64 object-contain animate-gm-boom" />
                        <div className="absolute -inset-10 bg-gradient-to-r from-transparent via-white/50 to-transparent rotate-45 animate-gm-flash pointer-events-none" />
                    </div>
                </div>
            )}

            {/* ── HERO PHOTO ─────────────────────────────────────────── */}
            <div className="relative shrink-0 h-[42%] overflow-hidden">
                <img
                    src={BG_PHOTO}
                    alt="Gruppomimo"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                />
                {/* gradient bas → fond crème */}
                <div
                    className="absolute inset-0"
                    style={{ background: `linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, ${brand.colors.pageBg} 100%)` }}
                />

                {/* LOGO centré dans le hero */}
                <div className={`absolute bottom-0 left-0 right-0 flex justify-center pb-2 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                    <BrandLogo className="h-24 w-auto object-contain drop-shadow-[0_4px_16px_rgba(0,0,0,0.35)]" />
                </div>
            </div>

            {/* ── CONTENU ────────────────────────────────────────────── */}
            <div className="flex-1 overflow-y-auto pb-48 hide-scrollbar px-5">
                <div className="flex flex-col items-center max-w-sm mx-auto pt-4 gap-5">

                    {/* AFFICHE DU MATCH */}
                    <div className={`flex items-center justify-center gap-5 w-full transition-all duration-700 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'} stagger-1`}>
                        <TeamDisplay {...teamProps} team={homeTeam} />
                        <div
                            className="w-11 h-11 rounded-full flex items-center justify-center shadow-xl shrink-0"
                            style={{ backgroundColor: brand.colors.accent }}
                        >
                            <span className="text-base font-heading font-black italic text-white">VS</span>
                        </div>
                        <TeamDisplay {...teamProps} team={awayTeam} />
                    </div>

                    {/* LOT À GAGNER */}
                    <div className={`relative w-full transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} stagger-2`}>
                        {/* Badge */}
                        <div
                            className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 font-black text-[10px] px-4 py-1 rounded-full uppercase tracking-[0.2em] shadow-sm whitespace-nowrap"
                            style={{ backgroundColor: brand.colors.highlight, color: brand.colors.highlightText }}
                        >
                            {t('holy.welcome.reward_badge')}
                        </div>

                        <div
                            className="relative overflow-hidden rounded-[2rem] p-5 flex flex-row items-center gap-4 border"
                            style={{
                                backgroundColor: 'rgba(255,255,255,0.55)',
                                backdropFilter: 'blur(16px)',
                                borderColor: 'rgba(255,255,255,0.5)',
                                boxShadow: '0 12px 40px -4px rgba(0,0,0,0.08)',
                            }}
                        >
                            {welcome.rewardImageUrl && (
                                <div className="flex-shrink-0 w-[45%] flex items-center justify-center animate-gm-float">
                                    <img
                                        src={welcome.rewardImageUrl}
                                        alt={welcome.rewardTitle}
                                        className="h-28 w-auto object-contain drop-shadow-[0_8px_16px_rgba(0,0,0,0.18)]"
                                    />
                                </div>
                            )}
                            <div className="flex flex-col items-start justify-center gap-1">
                                <h2
                                    className="font-heading text-[2.6rem] uppercase leading-none tracking-tighter italic"
                                    style={{ color: brand.colors.accent }}
                                >
                                    {welcome.rewardTitle}
                                </h2>
                                <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: `${brand.colors.accent}80` }}>
                                    {welcome.rewardSubtitle}
                                </span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* ── FOOTER CTA ─────────────────────────────────────────── */}
            <div
                className={`absolute bottom-0 left-0 w-full px-5 pb-6 pt-20 z-30 transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} stagger-3`}
                style={{ background: `linear-gradient(to top, ${brand.colors.pageBg} 60%, transparent)` }}
            >
                <div className="max-w-sm mx-auto flex flex-col items-center">
                    <button
                        id="demo-btn-start"
                        onClick={onStart}
                        className="group w-full flex items-center justify-center gap-3 py-5 rounded-full font-heading text-2xl uppercase italic tracking-wider shadow-lg active:translate-y-1 active:shadow-none transition-all"
                        style={{ backgroundColor: brand.colors.accent, color: brand.colors.accentText }}
                    >
                        {t('holy.welcome.cta')}
                        <ChevronRight size={28} strokeWidth={3} className="group-hover:translate-x-1 transition-transform" />
                    </button>

                    <footer className="flex items-center justify-center gap-3 mt-4 opacity-50">
                        <button className="text-[9px] font-black uppercase tracking-[0.15em]" style={{ color: brand.colors.accent }}>{t('footer.rules')}</button>
                        <span className="w-1 h-1 rounded-full" style={{ backgroundColor: `${brand.colors.accent}40` }} />
                        <button className="text-[9px] font-black uppercase tracking-[0.15em]" style={{ color: brand.colors.accent }}>{t('footer.cgps')}</button>
                        <span className="w-1 h-1 rounded-full" style={{ backgroundColor: `${brand.colors.accent}40` }} />
                        <button className="text-[9px] font-black uppercase tracking-[0.15em]" style={{ color: brand.colors.accent }}>{t('footer.legal')}</button>
                    </footer>
                </div>
            </div>
        </div>
    );
};
